
import React, { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NewReagentForm from "@/components/reagents/NewReagentForm";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotLoggedInMessage from "@/components/auth/NotLoggedInMessage";
import { fetchCategories } from "@/services/categoryService";
import { useAuth } from "@/contexts/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import CSVUploadModal from "@/components/reagents/CSVUploadModal";

const NewUploadReagentPage = () => {
  const { user, isLoading: isCheckingAuth } = useAuth();
  const [categories, setCategories] = useState<Array<{id: string, name: string}>>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCSVModalOpen, setIsCSVModalOpen] = useState(false);
  
  // Fetch categories only once when the component mounts and the user is authenticated
  useEffect(() => {
    // Don't fetch categories until we know if the user is authenticated
    if (isCheckingAuth) return;
    
    let isMounted = true;
    
    const getCategories = async () => {
      try {
        if (!user || !isMounted) return;
        
        setIsLoadingCategories(true);
        console.log("Fetching categories once...");
        const data = await fetchCategories();
        
        if (!isMounted) return;
        
        if (!data || data.length === 0) {
          const fallbackCategories = [
            { id: "default-chemicals", name: "Chemicals" },
            { id: "default-antibodies", name: "Antibodies" },
            { id: "default-enzymes", name: "Enzymes" },
            { id: "default-other", name: "Other" }
          ];
          setCategories(fallbackCategories);
          setError("No categories found. Using default values.");
        } else {
          setCategories(data);
        }
      } catch (error) {
        if (!isMounted) return;
        
        // Set some default categories as fallback
        const fallbackCategories = [
          { id: "default-chemicals", name: "Chemicals" },
          { id: "default-antibodies", name: "Antibodies" },
          { id: "default-enzymes", name: "Enzymes" },
          { id: "default-other", name: "Other" }
        ];
        
        setCategories(fallbackCategories);
        setError("Failed to load categories. Using default values.");
        toast.error("Failed to load categories. Using default values.");
      } finally {
        if (isMounted) {
          setIsLoadingCategories(false);
        }
      }
    };
    
    if (user) {
      getCategories();
    } else {
      setIsLoadingCategories(false);
    }
    
    return () => {
      isMounted = false;
    };
  }, [user, isCheckingAuth]);

  const handleCSVUploadSuccess = () => {
    toast.success("Reagents uploaded successfully from CSV!");
  };

  // If authentication is still being checked, show a loading indicator
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="container mx-auto px-4 py-8 flex-grow flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner />
            <p className="mt-4">Verifying authentication...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="container mx-auto px-4 py-8 flex-grow">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Upload Reagent</h1>
          
          {user && (
            <Button 
              onClick={() => setIsCSVModalOpen(true)}
              className="flex items-center gap-2"
            >
              <Upload size={16} />
              Bulk Upload (CSV)
            </Button>
          )}
        </div>
        
        {error && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
            <p className="mt-2 text-sm">Continuing with default categories.</p>
          </div>
        )}
        
        {user ? (
          isLoadingCategories ? (
            <div className="text-center py-12">
              <LoadingSpinner />
              <p className="mt-4">Loading categories...</p>
            </div>
          ) : (
            <NewReagentForm categories={categories} />
          )
        ) : (
          <NotLoggedInMessage />
        )}
      </div>
      
      <CSVUploadModal 
        open={isCSVModalOpen}
        onClose={() => setIsCSVModalOpen(false)}
        onSuccess={handleCSVUploadSuccess}
      />
      
      <Footer />
    </div>
  );
};

export default NewUploadReagentPage;
